/**
 * CEX terminal floating UI: modals + OB popovers — frosted gray panel, rounded
 * corners, subtle border (matches Lotos chart depth tooltip).
 */

/** Backdrop for portal modals — solid neutral dim (no backdrop-blur: avoids blue tint over charts) */
export const TERM_MODAL_OVERLAY = 'bg-black/80'

/**
 * Shared glass surface: semi-transparent gray + blur so the chart/content shows
 * through slightly. Use with rounded-2xl on the composed exports below.
 */
const TERM_SURFACE =
  'border border-white/10 bg-zinc-900/80 text-zinc-100 shadow-[0_10px_15px_-3px_rgba(0,0,0,0.45),0_4px_6px_-4px_rgba(0,0,0,0.35)] backdrop-blur-md supports-[backdrop-filter]:bg-zinc-900/75'

/** CEX toast stack — same glass family as modals, compact shadow */
export const TERM_TOAST_GLASS =
  'rounded-xl border border-white/10 bg-zinc-900/80 text-zinc-100 backdrop-blur-md supports-[backdrop-filter]:bg-zinc-900/75 shadow-[0_4px_14px_rgba(0,0,0,0.4)]'

/**
 * Centered dialog shell (max-w-sm) — same family as order-filter popover.
 */
export const TERM_MODAL_PANEL = `relative w-full max-w-sm rounded-2xl p-6 ${TERM_SURFACE}`

/**
 * Chart crosshair: OHLC + vol + notional (top-left) — same glass as modals, compact.
 */
export const TERM_CHART_HUD = `max-w-[min(16rem,88vw)] rounded-xl border border-slate-200/80 bg-white/85 p-2.5 text-left font-mono text-[0.7875rem] leading-snug text-slate-800 shadow-sm backdrop-blur-md supports-[backdrop-filter]:bg-white/80 dark:border-white/10 dark:bg-zinc-900/80 dark:text-zinc-100 dark:shadow-[0_4px_12px_rgba(0,0,0,0.35)] supports-[backdrop-filter]:dark:bg-zinc-900/75`

/**
 * Open orders filter popover.
 */
/** Open orders filter — tight to Min/Max fields (narrower shell, less padding). */
export const TERM_MODAL_POPOVER = `w-[14rem] rounded-xl px-2.5 py-2 ${TERM_SURFACE}`

/**
 * Order book: depth + selection popovers (fixed overlay; add width/position in JSX).
 */
export const TERM_OB_FLOAT_PANEL = `rounded-2xl p-4 font-sans text-[0.7875rem] leading-relaxed text-zinc-100 ${TERM_SURFACE}`

/** Flat field — Min/Max in filter */
export const TERM_MODAL_INPUT =
  'w-full rounded-lg border-0 bg-zinc-800/90 px-2 py-1.5 text-[0.7875rem] text-zinc-200 placeholder-zinc-500/90 focus:outline-none focus:ring-1 focus:ring-zinc-500/40'

/** Setup decimals / custom pair */
export const TERM_MODAL_INPUT_LG =
  'mt-2 w-full rounded-lg border-0 bg-zinc-800/90 px-3 py-2 font-mono text-[0.7875rem] text-zinc-100 placeholder-zinc-500/90 focus:outline-none focus:ring-1 focus:ring-zinc-500/40'

export const TERM_MODAL_CHECKBOX =
  'h-3.5 w-3.5 rounded border-zinc-500/50 bg-zinc-800/90 focus:ring-1 focus:ring-zinc-500/40'

export const TERM_MODAL_LABEL_SM = 'mb-1.5 text-[0.7875rem] font-medium text-zinc-100'
export const TERM_MODAL_LABEL_MD = 'block text-[0.7875rem] font-medium text-zinc-200'

export const TERM_MODAL_SUBTITLE = 'mt-0.5 text-[0.7875rem] uppercase tracking-wider text-zinc-500'

export const TERM_MODAL_LINK_MUTED =
  'w-full text-center text-[0.7875rem] text-zinc-400 underline decoration-zinc-600/80 underline-offset-2 hover:text-zinc-200'

export const TERM_MODAL_CLOSE_BTN =
  'absolute right-3 top-3 rounded-md p-1.5 text-zinc-500 hover:bg-zinc-800/80 hover:text-zinc-200'

export const TERM_MODAL_GHOST_BTN =
  'w-full rounded-md border border-zinc-700/60 bg-transparent px-3 py-2 text-[0.7875rem] text-zinc-400 hover:bg-zinc-800/50 hover:text-zinc-200'

export const TERM_MODAL_SUBMIT_BTN =
  'w-full rounded-md bg-emerald-600 px-3 py-2.5 text-[0.7875rem] font-semibold text-white shadow-sm hover:bg-emerald-500'
